<template>
  <v-container fluid>
    <v-layout row wrap>
      <v-flex class="d-flex justify-space-between mb-3 align-center" xs12>
        <h3 class="font-weight-bold">{{ routeLabel }}</h3>
        <div class="text-xs-right d-flex align-center">
          <v-btn color="blue darken-3" dark small class="mr-2" @click="handleExport" :loading="exporting">
            <v-icon left small>download</v-icon>
            Export
          </v-btn>
          <refresh-button :click-function="fetchData" />
        </div>
      </v-flex>

      <v-flex xs12>
        <v-card>
          <v-layout class="py-2 px-3" row wrap align-center>
            <v-flex xs12 sm4 class="pr-2">
              <v-flex class="d-flex align-center">
                <custom-date-range-picker @change="fetchData"
                                          v-model="filters.date_range"></custom-date-range-picker>
              </v-flex>
            </v-flex>

<!--            <v-flex xs12 sm4 class="pr-2">-->
<!--              <v-autocomplete v-model="filters.country" :items="countries" item-text="country_name" item-value="id"-->
<!--                              label="Country" prepend-inner-icon="flag" chips deletable-chips multiple outlined dense-->
<!--                              hide-details clearable @change="fetchData" />-->
<!--            </v-flex>-->

            <v-flex xs12 sm4>
              <v-autocomplete
                  :elevation="0"
                  flat
                  v-model="filters.office_id"
                  :items="offices"
                  item-text="name"
                  item-value="id"
                  label="Filter by office"
                  prepend-inner-icon="filter_list"
                  solo
                  hide-details
                  clearable
                  @change="fetchData"
              />
            </v-flex>
          </v-layout>

          <v-divider />

          <v-card-text class="pa-0">
            <v-data-table :headers="headers" :items="rows" :loading="loading" :options.sync="pagination"
                          :server-items-length="serverTotal" :footer-props="footerProps" item-key="id">
              <template v-slot:item.s_no="{ index }">
                <td>{{ (pagination.page - 1) * pagination.itemsPerPage + index + 1 }}</td>
              </template>
              <template v-slot:body.append>
                <tr class="grey lighten-2 font-weight-bold">
                  <td>-</td>
                  <td>Total</td>
                  <td>-</td>
                  <td v-for="field in totalFields" :key="field" class="text-right">{{ totals[field] }}</td>
                  <td>-</td>
                </tr>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <v-dialog v-model="exportDialog" persistent width="500">
      <v-card>
        <v-toolbar color="blue darken-3" dark>
          <v-toolbar-title>Export Options</v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="exportDialog = false"><v-icon>close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-form ref="exportFormRef" v-model="exportValid" lazy-validation>
            <v-text-field v-model="exportName" label="Name" outlined />
            <v-text-field v-model="exportRows" :rules="[rules.required]" label="Number of rows to export *" type="number" outlined />
          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn color="red lighten-1" class="white--text" @click="exportDialog = false">Cancel</v-btn>
          <v-btn color="blue darken-3" class="white--text" :loading="exporting" @click="handleExportClicked">Export</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Http from "axios";
import { VERSIONED_API_URL } from "../../config/config";
import RefreshButton from "@/components/RefreshButton";
import customDateRangePicker from "../../components/CustomDateRangePicker.vue";
export default {
  name: "SummaryReportVisaVisitRevenue",
  components: { RefreshButton,customDateRangePicker },

  data() {
    const currentDate = new Date().toISOString().split("T")[0];

    return {
      rows: [], loading: false, serverTotal: 0,
      filters: { date_range: { start: currentDate, end: currentDate }, country: [], office_id: null },
      dateMenu: false, dateRangeValues: [currentDate, currentDate],
      pagination: { page: 1, itemsPerPage: 25, sortBy: ["created_at"], sortDesc: [true] },
      footerProps: { "items-per-page-options": [10, 25, 50, 100] },
      exportDialog: false, exporting: false, exportValid: true, exportName: "", exportRows: 500,
      rules: { required: (v) => !!v || "This field is required" },
      totalFields: ["total_visa", "total_visit", "visa_fee_usd", "late_fee_usd", "penalty_npr", "amount_usd", "amount_npr"],
      headers: [
        { text: "S.N.",           value: "s_no",            sortable: false },
        { text: "Visa Type",      value: "visa_type_name", sortable: false },
        { text: "Visit Type",     value: "visit_type",    sortable: false },
        { text: "Total Visa",     value: "total_visa",    sortable: false, align: "right" },
        { text: "Total Visit",    value: "total_visit",   sortable: false, align: "right" },
        { text: "Visa Fee (USD)", value: "visa_fee_usd",  sortable: false, align: "right" },
        { text: "Late Fee (USD)", value: "late_fee_usd",  sortable: false, align: "right" },
        { text: "Penalty (NPR)",  value: "penalty_npr",   sortable: false, align: "right" },
        { text: "Amount (USD)",   value: "amount_usd",    sortable: false, align: "right" },
        { text: "Amount (NPR)",   value: "amount_npr",    sortable: false, align: "right" },
        { text: "Date",           value: "date_for",      sortable: false },
      ],
    };
  },

  computed: {
    ...mapGetters({ countries: "CountryList/all", offices: "OfficeList/offices" }),
    routeLabel() { return (this.$route && this.$route.meta && this.$route.meta.label) || "Visa Visit Revenue Summary"; },
    formattedDateRange() {
      const { start, end } = this.filters.date_range || {};
      if (start && end) return `${start} ~ ${end}`;
      return start || "";
    },
    totals() {
      const result = {};
      this.totalFields.forEach((f) => (result[f] = 0));
      this.rows.forEach((row) => this.totalFields.forEach((f) => { result[f] += parseFloat(row[f]) || 0; }));
      Object.keys(result).forEach((k) => {
        if (!["total_visa", "total_visit"].includes(k)) result[k] = result[k].toFixed(2);
      });
      return result;
    },
  },

  watch: {
    pagination: { deep: true, handler() { this.fetchData(); } },
  },

  methods: {
    ...mapActions({ fetchCountries: "CountryList/fetchCountries", fetchOffices: "OfficeList/fetchData" }),

    buildParams() {
      const { page, itemsPerPage, sortBy, sortDesc } = this.pagination;
      const { start, end } = this.filters.date_range || {};
      const params = { page, per_page: itemsPerPage, sort_by: (sortBy && sortBy[0]) || "created_at", descending: (sortDesc && sortDesc[0] !== undefined ? sortDesc[0] : true), paginate: true };
      if (start) params.start_date = start;
      if (end)   params.end_date   = end;
      if ((this.filters.country && this.filters.country.length)) params.country   = this.filters.country;
      if (this.filters.office_id)       params.office_id = this.filters.office_id;
      return params;
    },

    fetchData() {
      this.loading = true;
      Http.get(`${VERSIONED_API_URL}/summary-report-data/visa-visit-revenue`, { params: this.buildParams() })
          .then((res) => { this.rows = (res.data.data !== undefined ? res.data.data : res.data); this.serverTotal = (res.data.meta.total !== undefined ? res.data.meta.total : this.rows.length); })
          .catch((err) => console.error(err))
          .finally(() => { this.loading = false; });
    },

    onDateRangeChange(val) {
      if (val && val.length === 2) {
        const [a, b] = val.slice().sort();
        this.filters.date_range = { start: a, end: b };
        this.dateMenu = false;
        this.fetchData();
      }
    },

    handleExport() { this.exportDialog = true; },

    handleExportClicked() {
      if (!this.$refs.exportFormRef.validate()) return;
      this.exporting = true;
      Http.post(`${VERSIONED_API_URL}/summary-report-data/visa-visit-revenue/export`,
          { ...this.buildParams(), name: this.exportName, per_page: this.exportRows }, { responseType: "blob" })
          .then((res) => {
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(new Blob([res.data]));
            link.setAttribute("download", `${this.exportName || "visa-visit-revenue"}.xlsx`);
            document.body.appendChild(link); link.click(); link.remove();
            this.exportDialog = false;
          })
          .catch((err) => console.error(err))
          .finally(() => { this.exporting = false; });
    },
  },

  mounted() {
    if (this.$store.state.CountryList && this.$store.state.CountryList.all && this.$store.state.CountryList.all.length === 0) this.fetchCountries();
    this.fetchOffices();
    this.fetchData();
  },
};
</script>

<style scoped>
.font-weight-bold td { font-weight: 700; }
</style>